import { capitalize } from 'lodash'
import { Divider } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Text } from 'shared/components/Text/Text'
import { formatCurrency } from 'shared/helpers/formatters'
import { HandlerOf } from 'shared/helpers/typeHelper'
import { useBreakpoint, useContainerBreakpoint } from 'shared/hooks/useBreakpoint'
import { breakpoints, breakpointsSize } from 'shared/theme/breakpoints'
import { kellyGreenD1, marigoldOrange, midGray } from 'shared/theme/colors'
import styled, { css } from 'styled-components'

import { isConsole } from '../../config/config'
import { replaceUnderscore, timeAgoFromMinutes } from '../../helpers/formatters'
import { containerQuery } from '../../helpers/utils'
import { getJobConsoleStatusDetail, JobStatusDetail } from '../../models/job'
import { Load } from '../../models/load'
import { LoadRate } from './LoadRate'
import { LoadTags } from './LoadTags'
import { MultistopPath } from './MultiStop/MultiStopPath'

interface LoadItemProps {
  load: Load
  children?: React.ReactNode
  handleOpen?: HandlerOf<Load>
  highlight?: boolean
  confirmedRate?: number
  jobStatus?: JobStatusDetail
  jobHumanStatus?: string
  jobHumanStatusColor?: string
  bidAmount?: number
  renderAssignedTo?: () => JSX.Element | null
  renderAdditionalTags?: () => JSX.Element | null
  renderBookmark?: (load: Load) => JSX.Element | null
  isJob?: boolean
  hideBookmarkIcon?: boolean
  removeCardLayout?: boolean
  hideRecommendedTag?: boolean
}

export function LoadListItem({
  load,
  handleOpen,
  highlight,
  children,
  confirmedRate,
  jobStatus,
  jobHumanStatus,
  jobHumanStatusColor,
  bidAmount,
  renderAssignedTo,
  isJob,
  hideBookmarkIcon,
  renderBookmark,
  removeCardLayout,
  renderAdditionalTags,
  hideRecommendedTag,
}: LoadItemProps) {
  const [containerRef, isMobile] = useContainerBreakpoint(breakpointsSize.md)
  const isXsMobile = useBreakpoint('xs')
  const equipment =
    load.equipment && load.equipment.length > 0 ? replaceUnderscore(load.equipment[0].toLowerCase()) : ''

  return (
    <LoadItemContainer
      className="load-item-container"
      data-testid="load-item"
      onClick={() => handleOpen?.(load)}
      $highlight={highlight}
      $numColumns={isJob ? 4 : 5}
      $removeCardLayout={removeCardLayout}
      $hasOnClick={!!handleOpen}
      ref={containerRef}
    >
      <Broker $isJob={isJob}>
        {children ? (
          <>{children}</>
        ) : (
          <>
            <Text variant="paragraph2" fontColor={midGray} fontWeight={400}>
              {load.providerDisplayName}
            </Text>

            {load.ageMin !== null && load.ageMin !== undefined && (
              <>
                {isMobile && !isJob && <Divider vertical margin="0 8px" />}
                <Text variant="label3" fontColor={midGray}>
                  {timeAgoFromMinutes(load.ageMin)}
                </Text>
              </>
            )}
          </>
        )}
      </Broker>

      <Tags>
        {!isJob ? (
          <LoadTags loadTags={load.loadTags || []} hideRecommendedTag={hideRecommendedTag} />
        ) : isMobile ? (
          <Text variant="header3" fontColor={jobHumanStatusColor}>
            {isConsole() && jobStatus ? getJobConsoleStatusDetail(jobStatus, jobHumanStatus) : jobHumanStatus}
          </Text>
        ) : null}
        {renderAdditionalTags?.()}
      </Tags>

      <Path>
        <MultistopPath
          isSmall
          deadhead={isJob ? undefined : load.totalDeadheadMi}
          stops={load.stops}
          maskData={load.maskedData}
        />
      </Path>

      <Equipment isColumn gap="8px" className="load-list-equipment">
        <Text variant="label3" fontColor={midGray}>
          {capitalize(equipment)}
        </Text>
        {isMobile && <Divider vertical />}
        <Text variant="label3" fontColor={midGray}>
          {load.tripDistanceMi} mi
        </Text>
        {load.truckWeightLb ? (
          <>
            {isMobile && <Divider vertical />}
            <Text variant="label3" fontColor={midGray}>
              {load.truckWeightLb} lbs
            </Text>
          </>
        ) : null}
      </Equipment>

      <Rate className="load-list-rate">
        <StyledLoadDetailsRate
          load={load}
          confirmedRate={confirmedRate}
          isJob={isJob}
          isInline={isMobile}
          isSmall
          wrapTripRate={isMobile}
          jobStatus={jobStatus}
        />
        {bidAmount ? (
          <Text variant="header3" fontColor={marigoldOrange} mt={1} align={isXsMobile ? 'right' : undefined}>
            Your bid {isXsMobile ? <br /> : ':'}
            {formatCurrency(bidAmount)}
          </Text>
        ) : null}
      </Rate>

      <Bookmark className="load-list-bookmark">
        {isJob
          ? renderAssignedTo?.()
          : // hide bookmark icon for contacted loads list
          hideBookmarkIcon || !renderBookmark
          ? null
          : renderBookmark(load)}
      </Bookmark>
    </LoadItemContainer>
  )
}

const LoadItemContainer = styled.div<{
  $highlight?: boolean
  $numColumns: 4 | 5
  $removeCardLayout?: boolean
  $hasOnClick?: boolean
}>`
  position: relative;
  display: grid;
  grid-template-columns: 3fr 5fr 1.4fr 2.2fr 32px;
  grid-template-rows: auto 1fr;
  gap: 0 20px;
  grid-template-areas:
    'broker path equipment rate bookmark'
    'tags path equipment rate bookmark';

  margin: 16px auto;
  padding: 16px;
  width: 100%;
  background: white;
  border: ${({ $highlight }) => ($highlight ? `1px solid ${kellyGreenD1}` : 'none')};
  border-radius: 6px;

  ${({ $removeCardLayout }) =>
    $removeCardLayout &&
    css`
      margin: 0;
      padding: 0;
      box-shadow: none;
      background: none;
      border: none;
    `};

  ${containerQuery(
    breakpoints.sm,
    css`
      grid-template-columns: 1fr auto;
      grid-template-rows: auto;
      grid-template-areas:
        'tags bookmark'
        'path bookmark'
        'path rate'
        'equipment rate'
        'broker rate';
      gap: 10px;
    `
  )}

  @media ${breakpoints.xs} {
    padding: 16px 20px;
  }

  &:hover {
    ${({ $hasOnClick }) => $hasOnClick && 'cursor: pointer;'}
  }
`

const StyledLoadDetailsRate = styled(LoadRate)`
  ${containerQuery(
    breakpoints.sm,
    css`
      flex-direction: column-reverse;
      justify-content: flex-start;
      align-items: flex-end;

      > div:first-child {
        text-align: right;
      }

      & div {
        justify-content: flex-end;
        align-items: flex-end;
        text-align: right;
        margin-bottom: 0;
      }
    `
  )}
`

const Tags = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: tags;
`

const Broker = styled.div<{ $isJob?: boolean }>`
  grid-area: broker;
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  gap: 5px;

  ${({ $isJob }) =>
    $isJob &&
    css`
      justify-content: space-between;
      height: 100%;
    `}

  ${containerQuery(
    breakpoints.sm,
    css`
      margin-bottom: 0;
      flex-direction: row;
      justify-content: flex-start;
    `
  )}
`

const Path = styled.div`
  grid-area: path;
  min-width: 312px;

  ${containerQuery(
    breakpoints.sm,
    css`
      min-width: 0;
    `
  )}
`

const Equipment = styled(FlexBox)`
  grid-area: equipment;
  display: flex;
  flex-direction: column;

  ${containerQuery(
    breakpoints.sm,
    css`
      flex-direction: row;
    `
  )}

  @media ${breakpoints.xs} {
    top: 16px;
    right: 20px;
  }
`

const Rate = styled.div`
  width: 100%;
  grid-area: rate;
  margin-left: auto;

  ${containerQuery(
    breakpoints.sm,
    css`
      margin-top: 0;
      margin-left: 0;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: flex-end;
    `
  )}
`

const Bookmark = styled.div`
  grid-area: bookmark;
  margin-left: auto;

  & div {
    margin: 0;
  }

  ${containerQuery(
    breakpoints.sm,
    css`
      margin-top: 0;
      margin-left: 0;

      & > div {
        position: static;
        display: flex;
        justify-content: flex-end;
      }
    `
  )}
`
